// src/pdf/vitPdf.js
//
// Generazione del Verbale di Intervento Tecnico (VIT).
// Modulo condiviso frontend/backend: riceve i byte del template PDF
// e restituisce i byte del PDF compilato. Nessun accesso al filesystem.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  createWriters,
  embedSignature,
  sanitizeText,
  toItalianDateString,
} from './pdfShared.js';
import { removeSupersededObjects } from './pdfSanitize.js';

/** Nome del file template in public/VIT/ */
export function vitTemplateFileName(template) {
  return template === 'CIESSE_SERVICE' ? 'CiesseService.pdf' : 'CiesseImpianti.pdf';
}

/**
 * @param {Object} params
 * @param {Uint8Array|ArrayBuffer} params.templateBytes byte del PDF template
 * @param {Object} params.formData
 * @param {string=} params.tecnicoSignature data URL PNG
 * @param {string=} params.clienteSignature data URL PNG
 * @returns {Promise<Uint8Array>}
 */
export async function buildVitPdf({
  templateBytes,
  formData = {},
  tecnicoSignature,
  clienteSignature,
}) {
  const pdfDoc = await PDFDocument.load(templateBytes);
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const { width, height } = firstPage.getSize();

  const { writeText } = createWriters(firstPage, font, {
    fontSize: 10,
    color: rgb(0, 0, 0),
  });

  // ---------------- 1) Dati intervento ----------------
  const {
    data,
    cliente,
    impianto,
    luogo,
    telefono,
    numeroIntervento,
    matricolaImpianto,
    modello,
    inventario,
    codiceInterno,

    // Box costi (compatibile con vecchio e nuovo frontend)
    dirittoFissoDescrizione,
    dirittoFissoEuro,
    oreLavoroDescrizione,
    oreLavoroQuantita,
    oreLavoroEuro,

    // Ricambi (max 8 righe)
    ricambi = [],

    // Descrizione lavori
    lavoriEseguiti,
  } = formData;

  const oreQty = oreLavoroQuantita || oreLavoroDescrizione || '';
  const dataIt = toItalianDateString(data);

  writeText(dataIt || '', 270, height - 770);            // Data
  writeText(cliente || '', 300, height - 235);           // Cliente
  writeText(impianto || '', 300, height - 255);          // Impianto
  writeText(luogo || '', 300, height - 270);             // Luogo
  writeText(telefono || '', 300, height - 290);          // Telefono
  writeText(numeroIntervento || '', 300, height - 215);  // N° intervento
  writeText(matricolaImpianto || '', 250, height - 340); // Matricola

  writeText(codiceInterno || '', 460, height - 65);      // Codice interno
  writeText(modello || '', 100, height - 340);           // Modello
  writeText(inventario || '', 400, height - 340);        // Inventario

  // ---------------- 2) Box costi: diritto fisso + ore lavoro ----------------
  writeText(dirittoFissoDescrizione || '1', 340, height - 400);
  writeText(dirittoFissoEuro || '', width - 160, height - 400);

  const oreLabel = oreQty ? `${oreQty}` : '';
  writeText(oreLabel, 340, height - 420);
  writeText(oreLavoroEuro || '', width - 160, height - 420);

  // ---------------- 3) Box ricambi (8 righe) ----------------
  const startRicambiY = height - 470;
  const rowHeight = 18;

  (ricambi || []).slice(0, 8).forEach((riga, index) => {
    const y = startRicambiY - index * rowHeight;
    writeText(riga.quantita || '', 100, y);
    writeText(riga.descrizione || '', 150, y);
    writeText(riga.euro || '', width - 140, y);
  });

  // ---------------- 4) Calcolo totale euro ----------------
  const parseEuro = (val) => {
    if (val === undefined || val === null || val === '') return null;
    const n = parseFloat(String(val).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const euroValues = [];
  const df = parseEuro(dirittoFissoEuro);
  if (df !== null) euroValues.push(df);
  const ol = parseEuro(oreLavoroEuro);
  if (ol !== null) euroValues.push(ol);

  (ricambi || []).forEach((riga) => {
    const v = parseEuro(riga?.euro);
    if (v !== null) euroValues.push(v);
  });

  if (euroValues.length > 0) {
    const totale = euroValues.reduce((a, b) => a + b, 0);
    writeText(totale.toFixed(2), width - 140, height - 615);
  }

  // ---------------- 5) Lavori eseguiti ----------------
  if (lavoriEseguiti) {
    const maxLines = 22;        // righe max nel riquadro
    const lineHeight = 11;
    const maxCharsPerLine = 95;
    const startX = 75;
    let y = height - 668;       // box "DESCRIZIONE LAVORI ESEGUITI"

    const paragraphs = sanitizeText(lavoriEseguiti).split('\n');
    let lineCount = 0;
    let truncated = false;

    for (const para of paragraphs) {
      let text = para.trim();

      if (!text) {
        if (lineCount < maxLines) {
          y -= lineHeight;
          lineCount += 1;
        }
        continue;
      }

      while (text.length > 0 && lineCount < maxLines) {
        const chunk = text.slice(0, maxCharsPerLine);
        writeText(chunk, startX, y);
        text = text.slice(maxCharsPerLine);
        y -= lineHeight;
        lineCount += 1;
      }

      if (text.length > 0) {
        // non c'è più spazio ma rimane testo
        truncated = true;
        break;
      }
    }

    if (truncated && lineCount > 0) {
      writeText('...', startX + 30, y + lineHeight);
    }
  }

  // ---------------- 6) Firme ----------------
  await embedSignature(pdfDoc, firstPage, clienteSignature, width - 210, 45, 150, 60);
  await embedSignature(pdfDoc, firstPage, tecnicoSignature, 80, 45, 150, 60);

  // Va richiamata subito prima del save: vedi pdfSanitize.js
  removeSupersededObjects(pdfDoc);

  return pdfDoc.save();
}
